// ============================================
// STEP 3: USAGE DETAILS
// ============================================

import { useState, useEffect } from 'react';

import { ArrowLeft, ArrowRight, Home, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter, Button, Input, SelectionCard } from '../../../components/ui';

import { useFormStore } from '../../../store';
import { DEVICE_INTENT_OPTIONS, PRIMARY_USAGE_OPTIONS, DAILY_HOURS_OPTIONS, OS_PREFERENCE_OPTIONS, PORTABILITY_OPTIONS } from '../../../constants';
import styles from './Steps.module.css';
import type { PrimaryUsage, DailyUsageHours, PortabilityNeed, OsPreference } from '../../../types';

export function UsageDetailsStep() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const { formData, updateUsageDetails, nextStep, prevStep } = useFormStore();
  const { usageDetails } = formData;
  const [error, setError] = useState('');


  const handlePrimaryUsage = (value: PrimaryUsage) => {
    const currentUsage = usageDetails.primaryUsage;
    const newUsage = currentUsage.includes(value)
      ? currentUsage.filter(u => u !== value)
      : [...currentUsage, value];
    updateUsageDetails({ primaryUsage: newUsage });
    setError('');
  };
  const handleSecondaryUsage = (value: PrimaryUsage) => {
    const currentUsage = usageDetails.secondaryUsage || [];
    const newUsage = currentUsage.includes(value)
      ? currentUsage.filter(u => u !== value)
      : [...currentUsage, value];
    updateUsageDetails({ secondaryUsage: newUsage });
  };
  const handleDailyHours = (value: DailyUsageHours) => {
    updateUsageDetails({ dailyHours: value });
  };
  const handleNext = () => {
    // if (usageDetails.primaryUsage.length === 0) {
    //   setError('Please select at least one primary usage');
    //   setToast('Please select at least one primary usage');
    //   return;
    // }
    // if (!usageDetails.dailyHours) {
    //   setToast('Please select your daily usage hours.');
    //   return;
    // }
    nextStep();
  };

  return (
    <>
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>How will you use your device?</CardTitle>
          <CardDescription>
          Tell us about your daily workflow and computing needs.
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {/* Device Intent */}
        <div className={styles.sectionTitle}>
          <h4>What are you looking for?</h4>
        </div>
        <div className={styles.optionsGrid}>
          {DEVICE_INTENT_OPTIONS.map(option => (
            <SelectionCard
              key={option.value}
              selected={usageDetails.deviceIntent === option.value}
              onClick={() => updateUsageDetails({ deviceIntent: option.value })}
              title={option.label}
              description={option.description}
            />
          ))}
        </div>

        {/* Primary Usage */}
        <div className={styles.sectionTitle}>
          <h4>Primary use cases</h4>
          <p className={styles.sectionSubtitle}>Select your main tasks (at least one)</p>
          {error && <p className={styles.errorText}>{error}</p>}
        </div>
        <div className={styles.optionsRow}>
          {PRIMARY_USAGE_OPTIONS.map(option => (
            <button
              key={option.value}
              type="button"
              className={`${styles.chipButton} ${usageDetails.primaryUsage.includes(option.value as PrimaryUsage) ? styles.chipActive : ''}`}
              onClick={() => handlePrimaryUsage(option.value as PrimaryUsage)}
              title={option.description}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Secondary Usage */}
        <div className={styles.sectionTitle}>
          <h4>Secondary / occasional uses</h4>
          <p className={styles.sectionSubtitle}>What else might you do sometimes?</p>
        </div>
        <div className={styles.optionsRow}>
          {PRIMARY_USAGE_OPTIONS
            .filter(o => !usageDetails.primaryUsage.includes(o.value as PrimaryUsage))
            .map(option => (
              <button
                key={option.value}
                type="button"
                className={`${styles.chipButton} ${styles.chipSecondary} ${usageDetails.secondaryUsage?.includes(option.value as PrimaryUsage) ? styles.chipActive : ''}`}
                onClick={() => handleSecondaryUsage(option.value as PrimaryUsage)}
              >
                {option.label}
              </button>
            ))}
        </div>

        {/* Daily Hours */}
        <div className={styles.sectionTitle}>
          <h4>Daily usage hours</h4>
        </div>
        <div className={styles.optionsGrid}>
          {DAILY_HOURS_OPTIONS.map(option => (
            <SelectionCard
              key={option.value}
              selected={usageDetails.dailyHours === option.value}
              onClick={() => handleDailyHours(option.value as DailyUsageHours)}
              title={option.label}
              description={option.description}
              size="compact"
            />
          ))}
        </div>

        {/* Work From Home */}
        <div className={styles.sectionTitle}>
          <h4>Work environment</h4>
        </div>
        <div className={styles.toggleCards}>
          <button
            type="button"
            className={`${styles.toggleCard} ${usageDetails.workFromHome ? styles.toggleActive : ''}`}
            onClick={() => updateUsageDetails({ workFromHome: !usageDetails.workFromHome })}
          >
            <Home size={20} />
            <span>Work from Home</span>
            {usageDetails.workFromHome && <span className={styles.checkmark}>✓</span>}
          </button>
          <button
            type="button"
            className={`${styles.toggleCard} ${!usageDetails.workFromHome ? styles.toggleActive : ''}`}
            onClick={() => updateUsageDetails({ workFromHome: false })}
          >
            <Briefcase size={20} />
            <span>Office / On-site</span>
            {!usageDetails.workFromHome && <span className={styles.checkmark}>✓</span>}
          </button>
        </div>

        {/* Portability */}
        <div className={styles.sectionTitle}>
          <h4>How important is portability?</h4>
          <p className={styles.sectionSubtitle}>Do you travel or move around frequently?</p>
        </div>
        <div className={styles.optionsGrid}>
          {PORTABILITY_OPTIONS.map(option => (
            <SelectionCard
              key={option.value}
              selected={usageDetails.needsPortability === option.value}
              onClick={() => updateUsageDetails({ needsPortability: option.value as PortabilityNeed })}
              title={option.label}
              description={option.description}
              size="compact"
            />
          ))}
        </div>

        {/* OS Preference */}
        <div className={styles.sectionTitle}>
          <h4>Operating System preference</h4>
        </div>
        <div className={styles.optionsGrid}>
          {OS_PREFERENCE_OPTIONS.map(option => (
            <SelectionCard
              key={option.value}
              selected={usageDetails.osPreference === option.value}
              onClick={() => updateUsageDetails({ osPreference: option.value as OsPreference })}
              title={option.label}
              description={option.description}
              size="compact"
            />
          ))}
        </div>

        {/* Specific Software */}
        <div className={styles.sectionTitle}>
          <h4>Any specific software requirements?</h4>
          <p className={styles.sectionSubtitle}>e.g., AutoCAD, Tally, SAP, specific IDE</p>
        </div>
        <Input
          placeholder="List any specific software you need to run..."
          value={usageDetails.specificSoftware || ''}
          onChange={(e) => updateUsageDetails({ specificSoftware: e.target.value })}
        />
      </CardContent>

      <CardFooter className={styles.footerSpaceBetween}>
        <Button variant="outline" onClick={prevStep}>
          <ArrowLeft size={18} />
          Back
        </Button>
        <Button onClick={handleNext} size="lg">
          Continue
          <ArrowRight size={18} />
        </Button>
      </CardFooter>
      </Card>
    </>
  );
}
